import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { useScenarioListener } from '../../sockets/scenarios.util';
import { useAppQuery } from '../use-app-query.hook';
import { useInvalidateQueries } from '../use-invalidate-queries.hook';

export const useCalcScenarioSafes = ({
  scenarioId,
  enabled = true,
}: {
  enabled?: boolean;
  scenarioId: string;
}) => {
  const queryClient = useQueryClient();
  const { invalidateQuery: invalidateConversionSummary } = useInvalidateQueries(
    QueryKey.GET_CONVERSION_SUMMARY,
  );
  const { data, isLoading, refetch, isRefetching } = useAppQuery<
    {
      capitalOwned: number;
      postRoundCapitalOwned: number;
      safeId: string;
      sharesConverted: number;
    }[]
  >({
    queryKey: [QueryKey.CALC_SCENARIO_SAFES, scenarioId, enabled],
    queryFn: () =>
      ApiService.get({
        endpoint: BackendRoute.SCENARIOS,
        routePath: [scenarioId, 'safes', 'calc'],
      }),
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: !!scenarioId && enabled,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.CALC_SCENARIO_SAFES] });
  }, [queryClient]);

  useScenarioListener('safes-calculated', async () => {
    await queryClient.refetchQueries({
      queryKey: [QueryKey.CALC_SCENARIO_SAFES],
    });
    invalidateConversionSummary();
  });

  return {
    invalidateQuery,
    data,
    isLoading: isLoading || isRefetching,
    refetch,
  };
};
